//#region src/lib/glass.ts
/**
* DESIGN.md §11 — Liquid Glass surfaces.
*
* Class-string constants, composed with `cn()`. Since the owner's
* 2026-09-15 decision and spec 204 (CON-1278, DESIGN.md §11.1 amendment
* 2026-10-18) glass goes on the surfaces a person acts on and on
* everything that floats: `glassPanel` for Card, Dialog, the auth card and
* dashboard widgets; `glassMenu` for menus, popovers and toasts;
* `glassSheet` for full-viewport overlays; `glassNav` / `glassFloat` /
* `glassContent` for the shell. Tables, alerts, banners and fields stay
* flat insets inside a glass frame. Never compose glass by hand
* (`bg-card/60 backdrop-blur-md`) — use these.
*
* Every color rides a `--glass-*` token from tokens.css (all color-mix
* over the existing palette). The `reduce-transparency:` variant
* (defined beside the tokens) swaps to a solid background and drops the
* backdrop-filter for users with transparency reduced.
*/
/**
* Floating glass panel — dialogs, dropdown menus, the auth card.
* Blur 26 / saturate 1.9 / 62% fill (`--glass-fill`), rim, float+highlight shadow, and a
* top-left specular sheen via `before:` (hence `isolate` + the sheen's
* negative z-index — children stay above it with no extra classes).
*
* `rounded-panel` is the named 1rem radius (DESIGN.md §2.3, spec 204
* DEC-7): marketing.css replaces Tailwind's radius scale and defines
* `--radius-inset` / `--radius-panel` / `--radius-shell` after the reset,
* and every app (web, admin, auth, Storybook) loads it.
*/
const glassPanel = "relative isolate overflow-hidden rounded-panel border border-glass-rim bg-glass shadow-glass backdrop-blur-glass backdrop-saturate-[1.9] before:pointer-events-none before:absolute before:inset-0 before:z-[-1] before:content-[''] before:bg-[radial-gradient(70%_40%_at_20%_0%,var(--glass-sheen),transparent_70%)] reduce-transparency:bg-background reduce-transparency:shadow-lg reduce-transparency:backdrop-blur-none reduce-transparency:backdrop-saturate-100";
/**
* Spec 204 DEC-1 (CON-1304) — a glass TILE: the panel's fill, rim, float
* shadow and sheen with NO `backdrop-filter`. The default `Card` inside the
* content panel. The content panel is 85 % opaque, so a blur behind a card
* there refracts nothing and costs a compositing layer per card — six cards
* on a dashboard broke the DESIGN §11.4 budget of three blur layers per
* viewport. The §11.7 media cards shipped this recipe first. Standalone
* cards (over the canvas: the auth card, a picker page without the shell)
* and dialogs keep `glassPanel`'s blur — `<Card floating>`.
*/
const glassTile = "relative isolate overflow-hidden rounded-panel border border-glass-rim bg-glass shadow-glass before:pointer-events-none before:absolute before:inset-0 before:z-[-1] before:content-[''] before:bg-[radial-gradient(70%_40%_at_20%_0%,var(--glass-sheen),transparent_70%)] reduce-transparency:bg-background reduce-transparency:shadow-lg";
/**
* Edge-attached glass bar — header, sidebar, sheet, admin aside.
* Lighter blur (20) at a higher fill (52%) for legibility over whatever
* scrolls beneath; no radius/sheen — bars keep their existing border-b /
* border-r hairlines.
*/
const glassNav = "bg-glass-nav backdrop-blur-glass-nav backdrop-saturate-[1.6] reduce-transparency:bg-background reduce-transparency:backdrop-blur-none reduce-transparency:backdrop-saturate-100";
/**
* Floating MENU — dropdowns, selectors, command palettes. These hover
* over arbitrary CONTENT (not the canvas), so the fill ladder puts them
* near-opaque (92%): a readable surface with a whisper of the page
* behind it. A 42%-fill menu shipped in v1.49.0 and text shimmered
* through list rows — readability beats the effect on menus, always.
* Same rim/shadow/radius language as glassPanel, no sheen.
*/
const glassMenu = "overflow-hidden rounded-panel border border-glass-rim bg-glass-menu shadow-glass backdrop-blur-glass-nav backdrop-saturate-[1.4] reduce-transparency:bg-background reduce-transparency:backdrop-blur-none reduce-transparency:backdrop-saturate-100";
/**
* A full-viewport overlay — the full-screen dialog (CON-1272). The menu
* fill (92 %: it covers arbitrary content) and the menu blur, with no
* radius, rim or shadow: it has no edges to show.
*/
const glassSheet = "bg-glass-menu backdrop-blur-glass-nav backdrop-saturate-[1.4] reduce-transparency:bg-background reduce-transparency:backdrop-blur-none reduce-transparency:backdrop-saturate-100";
/**
* DESIGN.md §11.6 — FLOATING shell panel (Icon-Composer layout): the
* sidebar/header/content are detached rounded panels inset from the
* viewport over the vivid canvas, not edge-attached bars. Same glass
* family as glassNav but with the panel geometry (radius, rim, float
* shadow) since every edge is visible.
*/
const glassFloat = "rounded-shell border border-glass-rim bg-glass-nav shadow-glass backdrop-blur-glass-nav backdrop-saturate-[1.35] reduce-transparency:bg-background reduce-transparency:backdrop-blur-none reduce-transparency:backdrop-saturate-100";
/**
* The CONTENT panel of the floating shell — near-opaque so data-dense
* pages read on a calm ground (the reference's center canvas), with a
* whisper of the wallpaper at the edges. 85% is the §11.6 value; the
* code shipped 90% until spec 117 phase A (2026-09-08) reconciled it
* to the documented number.
*/
const glassContent = "rounded-shell border border-glass-rim bg-background/85 shadow-glass backdrop-blur-glass-nav backdrop-saturate-[1.15] reduce-transparency:bg-background reduce-transparency:backdrop-blur-none reduce-transparency:backdrop-saturate-100";
//#endregion
export { glassContent, glassFloat, glassMenu, glassNav, glassPanel, glassSheet, glassTile };
